// Levels made in the level builder, kept in this browser (IndexedDB, next to the cached maps).
// Nothing is uploaded unless the player publishes a level; export/import moves them as files.

import type { LevelDef } from '../maps/boxlevel/types';
import { validateLevel } from '../maps/boxlevel/types';

const DB_NAME = 'bhop-web-levels';
const STORE = 'levels';

/** The author's latest finished run of the level, needed to publish it. */
export interface LevelProof {
  /** levelKey() of the level the run finished. */
  key: string;
  replay: Uint8Array;
  ticks: number;
}

interface Stored {
  name: string;
  def: LevelDef;
  updatedAt: number;
  proof?: LevelProof;
  /** Share code once published. */
  published?: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available'));
        return;
      }
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE))
          req.result.createObjectStore(STORE, { keyPath: 'name' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('Could not open IndexedDB'));
      req.onblocked = () => reject(new Error('IndexedDB is blocked by another tab'));
    });
    dbPromise.catch(() => (dbPromise = null));
  }
  return dbPromise;
}

function done<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export interface LevelInfo {
  name: string;
  title: string;
  boxes: number;
  updatedAt: number;
  published: string | null;
}

export async function listLevels(): Promise<LevelInfo[]> {
  const db = await openDb();
  const all = (await done(db.transaction(STORE).objectStore(STORE).getAll())) as Stored[];
  return all
    .map((s) => ({
      name: s.name,
      title: s.def.title,
      boxes: s.def.boxes.length,
      updatedAt: s.updatedAt,
      published: s.published ?? null,
    }))
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function loadLevel(name: string): Promise<LevelDef | null> {
  const db = await openDb();
  const s = (await done(db.transaction(STORE).objectStore(STORE).get(name))) as Stored | undefined;
  return s?.def ?? null;
}

/** Change a stored level's record (keeps everything not changed). */
async function update(name: string, fn: (s: Stored | undefined) => Stored | null): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const st = tx.objectStore(STORE);
  const get = st.get(name);
  get.onsuccess = () => {
    const next = fn(get.result as Stored | undefined);
    if (next) st.put(next);
  };
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function saveLevel(def: LevelDef): Promise<void> {
  await update(def.name, (old) => ({ ...old, name: def.name, def, updatedAt: Date.now() }));
}

/** Remember a finished run of the level (for publishing). */
export async function saveProof(name: string, proof: LevelProof): Promise<void> {
  await update(name, (old) => (old ? { ...old, proof } : null));
}

export async function setPublished(name: string, code: string | null): Promise<void> {
  await update(name, (old) => {
    if (!old) return null;
    const next = { ...old };
    if (code) next.published = code;
    else delete next.published;
    return next;
  });
}

/** A stored level with its finished run and share code. */
export async function loadLevelRecord(
  name: string,
): Promise<{ def: LevelDef; proof: LevelProof | null; published: string | null } | null> {
  const db = await openDb();
  const s = (await done(db.transaction(STORE).objectStore(STORE).get(name))) as Stored | undefined;
  return s ? { def: s.def, proof: s.proof ?? null, published: s.published ?? null } : null;
}

export async function deleteLevel(name: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).delete(name);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Parse a level file. Throws with a readable message. */
export function parseLevelFile(text: string): LevelDef {
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch {
    throw new Error("That file isn't a level (not JSON).");
  }
  const err = validateLevel(v);
  if (err) throw new Error(err);
  return v as LevelDef;
}
